import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'
import {
  IconAlertTriangle,
  IconMessagePlus,
  IconMinus,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react'
import { TextArea } from '@/components/forms/TextArea'

import { Button } from '@/components/buttons/Button'
import { CopyButton } from '@/components/buttons/CopyButton'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import SelectionCommentPopover from '@/components/comments/SelectionCommentPopover'
import { textOffsetWithin } from '@/components/comments/selectionOffsets'
import { locateSnippet } from '@/components/comments/anchorSnippet'
import {
  createChangeRequest,
  fetchPromptVersions,
  resetPrompt,
  revertPrompt,
  savePrompt,
} from '@/reducers/voiceAgents/voiceAgents.actions'
import {
  setDrawerContentView,
  setDrawerVersionOverride,
  clearDrawerVersionOverride,
  setDrawerHistoryOpen,
  setFocusedChangeRequest,
} from '@/reducers/voiceAgents/voiceAgents.redux'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'

import { DrawerTabContext } from './DrawerCommentsPanel'

import { getActiveVersion, isReadOnlyForOverride, latestVersion } from '../drawerHelpers'
import { filterRequestsByTarget, isOpenRequest } from '../changeRequestHelpers'
import { composeSegmentsText } from '../promptOffsets'
import { useOverrideEditing } from '../hooks/useOverrideEditing'

import SegmentedPromptDisplay from './SegmentedPromptDisplay'
import SystemSegmentBar from './SystemSegmentBar'
import PromptVersionList from './PromptVersionList'
import RevertConfirmModal from './RevertConfirmModal'
import VersionViewingStrip from './VersionViewingStrip'

// =============================================================================
// PromptTab — the only edit affordance in POC. Composes:
//   - Version bar (Active prompt / Viewing older edit) + history toggle
//   - Optional version history list (newest-first)
//   - Optional viewing strip (when peeking a non-latest version)
//   - Optional diff view
//   - SegmentedPromptDisplay, OR PromptSegmentsEditor (one textarea per
//     visible segment, system sections locked in place) when in edit mode
//   - Action footer: Edit / Save / Cancel + Reset to default
//   - Revert confirmation modal
// =============================================================================

// The optional placement marker an author may put in a prompt to say where the
// generated LANGUAGE block goes. A literal BE token, not an interpolation.
// eslint-disable-next-line no-template-curly-in-string
const LANGUAGE_ANCHOR = '${language_instructions}'

// The editor keeps one draft per visible segment, keyed by segment index.
// Flattening the visible text into one string loses where each part sat
// between <system> blocks, and saving it back reordered the prompt (AVR-807).
const toEditableSegments = (segments, promptText) =>
  isEmpty(segments) ? [{ index: 0, type: 'visible', content: promptText }] : segments

const getDraftSeed = (editableSegments) =>
  Object.fromEntries(
    editableSegments
      .filter((segment) => segment.type === 'visible')
      .map((segment) => [segment.index, segment.content])
  )

// Rebuild the full prompt in segment order, substituting each visible segment's
// draft in place. System segments are re-wrapped untouched.
export const composeFullText = (draftByIndex, editableSegments) =>
  editableSegments
    .map((segment) =>
      segment.type === 'system'
        ? `<system>${segment.content}</system>`
        : draftByIndex[segment.index] ?? segment.content
    )
    .join('')

// Whitespace-only visible segments aren't worth a textarea; composeFullText
// still writes them back verbatim.
const isBlankSegment = (segment) => segment.type === 'visible' && isEmpty(segment.content.trim())

const MAX_EDITOR_ROWS = 14
const MIN_SECTION_ROWS = 3

// Parse ${...} refs from `draft` and return the identifiers not in legalNames.
// The substitution syntax matches the BE convention (${var_name}); identifier
// rules mirror DeclareVariableRequest on the BE. The regex lives inline so
// each call gets fresh iterator state — a shared g-flag regex would retain
// lastIndex across calls and silently skip matches.
const findUndeclaredRefs = (draft, legalNames) => {
  const found = new Set()
  for (const match of draft.matchAll(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g)) {
    if (!legalNames.has(match[1])) {
      found.add(match[1])
    }
  }
  return [...found]
}

// Inline banner listing ${...} refs in the draft that aren't declared yet.
// Renders nothing when there are none.
const UndeclaredRefsBanner = ({ names }) => {
  if (isEmpty(names)) {
    return null
  }
  const plural = names.length > 1
  return (
    <div
      className="voice-agents__prompt-editor-error"
      data-testid="prompt-editor-undeclared"
      role="alert"
    >
      <IconAlertTriangle size={14} />
      <span>
        Cannot save — undeclared variable{plural ? 's' : ''}:{' '}
        {names.map((name, index) => (
          <React.Fragment key={name}>
            {index > 0 && ', '}
            <code>{`\${${name}}`}</code>
          </React.Fragment>
        ))}
        . Add {plural ? 'them' : 'it'} from the Variables tab first.
      </span>
    </div>
  )
}

// Edit mode: each visible segment gets its own textarea, in prompt order, with
// the locked system sections between them where they actually sit.
const PromptSegmentsEditor = ({ editableSegments, draft, onChange }) => {
  // Keep the blanks when nothing else is editable, so there is always somewhere to type.
  const hasContent = editableSegments.some(
    (segment) => segment.type === 'visible' && !isBlankSegment(segment)
  )
  const shown = hasContent
    ? editableSegments.filter((segment) => !isBlankSegment(segment))
    : editableSegments
  const single = shown.filter((segment) => segment.type === 'visible').length === 1

  return shown.map((segment) => {
    if (segment.type === 'system') {
      return <SystemSegmentBar key={segment.index} segment={segment} />
    }
    const lineCount = segment.content.split('\n').length
    return (
      <TextArea
        key={segment.index}
        className="voice-agents__prompt-editor-textarea"
        value={draft[segment.index] ?? ''}
        onChange={(event) => {
          const { value } = event.target
          onChange((prev) => ({ ...prev, [segment.index]: value }))
        }}
        rows={
          single
            ? MAX_EDITOR_ROWS
            : Math.min(MAX_EDITOR_ROWS, Math.max(MIN_SECTION_ROWS, lineCount))
        }
        data-testid={single ? 'prompt-editor-textarea' : `prompt-editor-textarea-${segment.index}`}
        aria-label={single ? 'Prompt content' : `Prompt section ${segment.index + 1}`}
      />
    )
  })
}

// Action footer: Cancel/Save while editing, otherwise Reset-to-default when an
// override exists. Extracted so PromptTab's own complexity stays in budget.
const PromptFooter = ({
  editing,
  readOnly,
  hasOverride,
  isSaving,
  isResetting,
  undeclaredRefs,
  onCancel,
  onSave,
  onReset,
}) => {
  const saveTitle = !isEmpty(undeclaredRefs)
    ? `Add ${undeclaredRefs.map((name) => `\${${name}}`).join(', ')} from the Variables tab first`
    : undefined
  return (
    <div
      className={classNames('voice-agents__prompt-footer', {
        'voice-agents__prompt-footer--editing': editing,
      })}
    >
      {editing && !readOnly ? (
        <>
          <Button small onClick={onCancel} data-testid="prompt-cancel">
            Cancel
          </Button>
          <Button
            brand
            small
            onClick={onSave}
            // Empty (or whitespace-only) prompts are valid — "this node says
            // nothing" is a deliberate choice, e.g. a silent greeting.
            disabled={isSaving || !isEmpty(undeclaredRefs)}
            title={saveTitle}
            data-testid="prompt-save"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </>
      ) : (
        hasOverride &&
        !readOnly && (
          <Button
            basic
            small
            critical
            onClick={onReset}
            disabled={isResetting}
            data-testid="prompt-reset"
            title="Drop every override and fall back to the engineer default"
          >
            <IconTrash /> Reset to default
          </Button>
        )
      )}
    </div>
  )
}

const PromptTab = ({ node }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const drawer = useSelector((state) => state.voiceAgents.drawer)
  const fetchedVersions = useSelector((state) => state.voiceAgents.versionsByNodeId[node?.id] || [])
  const isSaving = useSelector((state) => Boolean(state.voiceAgents.loading.savePrompt))
  const isReverting = useSelector((state) => Boolean(state.voiceAgents.loading.revertPrompt))
  const isResetting = useSelector((state) => Boolean(state.voiceAgents.loading.resetPrompt))
  const isCommenting = useSelector((state) =>
    Boolean(state.voiceAgents.loading.createChangeRequest)
  )
  const changeRequestItems = useSelector((state) => state.voiceAgents.changeRequests.items)
  const promptDisplayRef = useRef(null)
  // The selection being commented on, lifted from the popover so it stays
  // painted while composing (the native selection collapses on textarea focus).
  const [draftRange, setDraftRange] = useState(null)
  // Commenting here is the same two steps as on a call transcript: press the
  // affordance, then pick the span. Unlike the transcript the text stays
  // selectable throughout — there is no playhead to protect here, and prompt
  // text is worth being able to copy — so arming is simply what decides whether
  // a selection is read as the start of a comment.
  const [commentArmed, setCommentArmed] = useState(false)
  // Stable identity: the popover re-registers its document listeners whenever
  // this changes.
  const { actionsSlot: headerActionsSlot } = useContext(DrawerTabContext)

  const disarmComment = useCallback(() => setCommentArmed(false), [])

  // `node.config.versions` is routinely empty in the config/node responses, so
  // the authoritative history comes from the dedicated /nodes/versions slice
  // (`fetchedVersions`). All version state derives from this one resolved list —
  // never from `node.config.versions` directly — so the list and the peek/
  // read-only gates can't disagree (the empty-active-prompt bug).
  const versions = !isEmpty(fetchedVersions) ? fetchedVersions : node?.config?.versions || []
  const override = drawer.versionOverrideByNodeId[node?.id]
  const activeVersion = getActiveVersion(versions, override)
  const latest = latestVersion(versions)
  const readOnly = isReadOnlyForOverride(versions, override)

  // Cache-first version fetch: only hit the BE when we don't already have
  // versions in the slice for this node. Save / revert / reset call
  // fetchPromptVersions themselves so post-write refreshes still happen —
  // this guard just kills the 4 wasted renders (loading-true, error-null,
  // setVersions, loading-false) that fired on every repeat node click.
  useEffect(() => {
    if (agentId && node?.id && isEmpty(fetchedVersions)) {
      dispatch(fetchPromptVersions(agentId, node.id))
    }
  }, [agentId, node?.id, fetchedVersions.length, dispatch])

  if (!node?.config) {
    return (
      <div className="voice-agents__drawer-tab" data-testid="drawer-tab-prompt">
        <p className="voice-agents__drawer-tab-hint">No prompt content for this node type.</p>
      </div>
    )
  }

  // Current version → render the node's segmented content (nice system-block
  // collapsing). Peeking an older version → the BE gives us only its raw
  // `content` (no per-version segments), so drop the current segments and let
  // SegmentedPromptDisplay fall back to that version's content. Without this,
  // peeking would keep showing the current segments instead of the old text.
  // The generated LANGUAGE block: the text is agent-level, but whether this node
  // receives it — and where it lands — is computed per node the same way the
  // resolver decides at call time. Peeking an older version shows that version's
  // text, and the block is not part of any stored version, so it stays hidden.
  const languageBlockApplied = !readOnly && node.config.languageBlockApplied === true
  const languageBlockText = agent?.languageBlock?.text ?? ''
  const { languageBlockPlacement } = node.config
  const languageBlockWarnings = node.config.languageBlockWarnings ?? []

  const segments = readOnly ? activeVersion?.segments || null : node.config.segments
  // Peeking shows THAT version's content verbatim — including an empty/reset/
  // revert override (content "" or null, normalized via ??). Using `||` here
  // fell back to the live prompt, so every empty version rendered as the
  // current prompt and peeking them looked like a no-op.
  const promptText = readOnly ? activeVersion?.content ?? '' : node.config.promptText || ''
  const editableSegments = toEditableSegments(segments, promptText)

  // Inline comments anchor a highlighted span of this node's prompt. They share
  // the prompt target with general comments, so the drawer list already shows
  // them — here we paint their spans back onto the segmented text and let the
  // user select a fresh span to comment on (read display only; offsets are
  // defined against the current segments, not an older peeked version).
  const { text: baseText, baseByIndex } = composeSegmentsText(segments, promptText)
  // Re-anchor each open inline comment by its snippet in the text actually shown
  // (offset = tie-breaker only). Comments whose snippet is gone resolve to null
  // and simply aren't painted. The resolved ranges feed both the highlight marks
  // and the popover's overlap check, so both stay consistent under drift.
  const anchoredMarks = filterRequestsByTarget(changeRequestItems, 'prompt', node.id)
    .filter(isOpenRequest)
    .filter((request) => request.highlight)
    .map((request) => {
      const range = locateSnippet(baseText, request.highlight.snippet, request.highlight.start)
      return range ? { id: request.id, start: range.start, end: range.end } : null
    })
    .filter(Boolean)

  // The pending selection paints as a non-interactive draft mark alongside the
  // saved-comment marks; both index into baseText, clipped per segment downstream.
  const displayMarks = isNil(draftRange)
    ? anchoredMarks
    : [...anchoredMarks, { id: 'draft', start: draftRange.start, end: draftRange.end, draft: true }]

  // Map a selection point to its absolute offset in baseText: find the enclosing
  // segment element, add the in-segment text offset to that segment's base.
  const offsetForPoint = (domNode, offsetInNode) => {
    const element = domNode.nodeType === Node.TEXT_NODE ? domNode.parentElement : domNode
    const segmentEl = element?.closest('[data-segment-index]')
    if (isNil(segmentEl)) {
      return null
    }
    const base = baseByIndex[Number(segmentEl.getAttribute('data-segment-index'))]
    if (isNil(base)) {
      return null
    }
    return base + textOffsetWithin(segmentEl, domNode, offsetInNode)
  }

  const resolvePromptOffsets = (range) => {
    const start = offsetForPoint(range.startContainer, range.startOffset)
    const end = offsetForPoint(range.endContainer, range.endOffset)
    if (isNil(start) || isNil(end)) {
      return null
    }
    return start <= end ? { start, end } : { start: end, end: start }
  }

  const handleCommentSubmit = async ({ start, end, snippet, message }) => {
    const result = await dispatch(
      createChangeRequest({
        requestType: 'inline',
        agentId,
        message,
        targetType: 'prompt',
        targetId: node.id,
        highlightStart: start,
        highlightEnd: end,
        highlightSnippet: snippet,
      })
    )
    // Saved comments live in the Comments view, so go there and show it rather
    // than leaving the writer looking at the prompt wondering where it went.
    // Only on success — a failure keeps the composer and its text.
    if (result) {
      dispatch(setDrawerContentView('comments'))
    }
    return result
  }

  const handleCommentClick = (requestId) => {
    dispatch(setDrawerContentView('comments'))
    dispatch(setFocusedChangeRequest(requestId))
  }

  // Legal substitution targets:
  //   - every declared variable on the agent (editable + runtime-injected)
  //   - every requiredVars entry on this node (covers runtime vars that
  //     aren't in the agent.variables array but still get injected at runtime)
  const legalNames = useMemo(() => {
    const fromVars = (agent?.variables || []).map((variable) => variable.name)
    const fromRequired = node?.config?.requiredVars || []
    return new Set([...fromVars, ...fromRequired])
  }, [agent, node])

  const {
    editing,
    draft,
    setDraft,
    revertTarget,
    setRevertTarget,
    resetConfirmOpen,
    setResetConfirmOpen,
    startEdit,
    cancelEdit,
    save,
    confirmRevert,
    confirmReset,
  } = useOverrideEditing({
    onSave: (draftText) =>
      dispatch(savePrompt(agentId, node.id, composeFullText(draftText, editableSegments))),
    onRevert: (version) =>
      dispatch(revertPrompt(agentId, node.id, version, node.config?.name || node.id)),
    onReset: () => dispatch(resetPrompt(agentId, node.id, node.config?.name || node.id)),
    onAfterMutate: () => dispatch(clearDrawerVersionOverride(node.id)),
  })

  // Peeking a version or opening the editor swaps the prompt display out from
  // under the comment layer. The popover releases itself on unmount, but the
  // armed flag lives here, so drop it with the thing it was armed against.
  useEffect(() => {
    if (readOnly || editing) {
      setCommentArmed(false)
    }
  }, [readOnly, editing])

  // Peeking an older version is read-only — drop out of edit mode when entering it.
  // cancelEdit isn't memoized, so depending on it would re-run this every render;
  // we key on readOnly alone so it fires only on the transition. Safe because
  // cancelEdit just calls the stable setEditing setter.
  useEffect(() => {
    if (readOnly) {
      cancelEdit()
    }
  }, [readOnly])

  // Parse ${...} refs from the current draft; surface any that don't resolve.
  const undeclaredRefs = useMemo(
    () => (editing ? findUndeclaredRefs(Object.values(draft).join('\n'), legalNames) : []),
    [draft, editing, legalNames]
  )

  const handlePeek = (versionNumber) => {
    if (!latest) {
      return
    }
    if (versionNumber === latest.version) {
      dispatch(clearDrawerVersionOverride(node.id))
    } else {
      dispatch(setDrawerVersionOverride({ nodeId: node.id, version: versionNumber }))
    }
  }

  const handleToggleHistory = () => {
    dispatch(setDrawerHistoryOpen(!drawer.historyOpen))
  }

  const versionCount = versions.length
  const { hasOverride } = node.config

  const editAction =
    readOnly || editing ? null : (
      <Button
        basic
        small
        onClick={() => startEdit(getDraftSeed(editableSegments))}
        disabled={isReverting || isResetting}
        data-testid="prompt-edit"
      >
        <IconPencil /> Edit
      </Button>
    )

  return (
    <div className="voice-agents__drawer-tab" data-testid="drawer-tab-prompt">
      {/* Rendered into the drawer header beside the Prompt/Comments toggle, so
          the tab doesn't spend a whole row on one right-aligned button. Falls
          back to its own row if the tab is ever used outside the panel. */}
      {editAction && (headerActionsSlot ? createPortal(editAction, headerActionsSlot) : editAction)}

      <div className="voice-agents__version-bar" data-testid="version-bar">
        <span className="voice-agents__version-history-label">Version history - Newest first</span>
        <Button
          basic
          small
          onClick={handleToggleHistory}
          disabled={isEmpty(versions)}
          className="voice-agents__version-history-toggle"
          data-testid="version-history-toggle"
        >
          {drawer.historyOpen ? <IconMinus /> : <IconPlus />}
          {drawer.historyOpen ? 'Hide edits' : 'Show edits'}
        </Button>
      </div>

      {drawer.historyOpen && versionCount > 0 && (
        <PromptVersionList
          versions={versions}
          latest={latest}
          activeVersion={activeVersion}
          onPeek={handlePeek}
        />
      )}

      {readOnly && activeVersion && latest && (
        <VersionViewingStrip
          version={activeVersion}
          onRevert={() => setRevertTarget(activeVersion)}
        />
      )}

      {editing && !readOnly ? (
        <div className="voice-agents__prompt-editor" data-testid="prompt-editor">
          <UndeclaredRefsBanner names={undeclaredRefs} />
          <PromptSegmentsEditor
            editableSegments={editableSegments}
            draft={draft}
            onChange={setDraft}
          />
          <div className="voice-agents__prompt-editor-hint">
            {/* eslint-disable-next-line no-template-curly-in-string */}
            Use <code>{'${var_name}'}</code> to reference a runtime variable. System sections are
            locked and stay in place when you save.
          </div>
        </div>
      ) : (
        <>
          {/* Reading the prompt is when you notice something worth saying about
              it; while editing it you change it instead. The affordance sits on
              the prompt rather than in the header above it, and sticks near the
              top of the view as a long prompt scrolls, so it stays in reach of
              whatever you are reading. Same elevated pill the selection
              affordance uses, so the two read as one thing. */}
          {!readOnly && !commentArmed && (
            <div className="voice-agents__prompt-comment-sticky">
              <span className="voice-agents__prompt-comment-trigger comment-selection-popover__toolbar">
                <Button
                  basic
                  circular
                  small
                  icon={<IconMessagePlus />}
                  tooltip="Comment on this prompt"
                  aria-label="Comment on this prompt"
                  data-testid="prompt-comment-trigger"
                  onClick={() => setCommentArmed(true)}
                />
              </span>
            </div>
          )}
          <SegmentedPromptDisplay
            segments={segments}
            fallbackText={promptText}
            containerRef={promptDisplayRef}
            marks={displayMarks}
            focusedRequestId={drawer.focusedRequestId}
            onCommentClick={handleCommentClick}
            armed={commentArmed}
          />
          <SelectionCommentPopover
            containerRef={promptDisplayRef}
            baseText={baseText}
            // Peeking an older version is read-only: the display shows that
            // version's text but a comment would store offsets against the
            // current node, so don't allow commenting while peeking.
            enabled={!readOnly}
            busy={isCommenting}
            onSubmit={handleCommentSubmit}
            resolveOffsets={resolvePromptOffsets}
            existingRanges={anchoredMarks}
            onOpenExisting={handleCommentClick}
            onPendingChange={setDraftRange}
            requiresArming
            armed={commentArmed}
            onDisarm={disarmComment}
          />
          {commentArmed && (
            <span className="voice-agents__prompt-comment-hint" data-testid="prompt-comment-hint">
              Select the words you want to comment on
            </span>
          )}
        </>
      )}

      <PromptFooter
        editing={editing}
        readOnly={readOnly}
        hasOverride={hasOverride}
        isSaving={isSaving}
        isResetting={isResetting}
        undeclaredRefs={undeclaredRefs}
        onCancel={cancelEdit}
        onSave={save}
        onReset={() => setResetConfirmOpen(true)}
      />

      {/* What the model receives that this editor does not hold: the backend
          generates the LANGUAGE block and injects it at call time. It sits
          below the controls, not between them and the prompt — it is a
          footnote about the whole editor, and wedging it above Save separated
          that button from the text it saves. Rendered outside the display for
          a second reason: inline comments anchor by character offset over the
          composed prompt text, and injected text would shift every one. */}
      {languageBlockApplied && !isEmpty(languageBlockText) && (
        <div
          className="voice-agents__generated-panel voice-agents__prompt-language-block"
          data-testid="prompt-language-block"
        >
          <div className="voice-agents__prompt-language-block-head">
            <span className="voice-agents__llm-field-label">Added at call time</span>
            <span className="voice-agents__llm-notes" data-testid="prompt-language-block-placement">
              {languageBlockPlacement === 'anchor'
                ? `At your ${LANGUAGE_ANCHOR} anchor above`
                : 'At the end of this prompt'}
            </span>
            {languageBlockPlacement !== 'anchor' && (
              <CopyButton
                content={LANGUAGE_ANCHOR}
                iconOnly
                text
                showTooltip
                tooltipContent="Copy the anchor"
                data-testid="prompt-language-block-copy-anchor"
              />
            )}
          </div>
          {/* The anchor is the only way to move the block, and it is named
              nowhere the author is working — the Variables tab lists it a tab
              away, under "All variables". Say it here, and stop once one is
              placed: the line above then already spells the anchor out.

              The heading half matters as much as the name. Appending writes a
              "## Language" heading; substituting at an anchor writes nothing
              around the block. So an author who adds an anchor to move the
              block loses that heading without being told — unless this says so
              before they do it. */}
          {languageBlockPlacement !== 'anchor' && (
            <p className="voice-agents__llm-notes" data-testid="prompt-language-block-hint">
              {`Paste ${LANGUAGE_ANCHOR} to place these rules yourself. With no anchor they are appended under a "## Language" heading; at an anchor they replace it as-is, with no heading added.`}
            </p>
          )}
          <p className="voice-agents__generated-text">{languageBlockText}</p>
          {languageBlockWarnings.map((warning) => (
            <ErrorMessage
              key={warning.code}
              warning={warning.severity !== 'error'}
              content={warning.message}
              data-testid={`prompt-language-block-warning-${warning.code}`}
            />
          ))}
        </div>
      )}

      <RevertConfirmModal
        show={Boolean(revertTarget)}
        node={node}
        latest={latest}
        targetVersion={revertTarget}
        onCancel={() => setRevertTarget(null)}
        onConfirm={confirmRevert}
        busy={isReverting}
      />

      <RevertConfirmModal
        show={resetConfirmOpen}
        node={node}
        latest={latest}
        targetVersion={
          // Reuse the modal's UI by treating "reset" as "revert to v1"
          // for copy purposes — the actual confirmed action below is the
          // resetPrompt thunk, which drops every override on the BE.
          !isEmpty(versions) ? versions[0] : null
        }
        onCancel={() => setResetConfirmOpen(false)}
        onConfirm={confirmReset}
        busy={isResetting}
      />
    </div>
  )
}

export default PromptTab
